<template>
  <Form @submit="submit"
        :validation-schema="applicationSchema" @invalid-submit="onInvalidSubmit">
    <section class="application-create__form">
      <div class="container-xxxl">
        <div class="row">
          <div class="col-xxl-6 offset-xxl-3">
            <h3 class="application__form__title">{{ $lang.app.GENERAL_INFORMATION }}</h3>
            <div class="application__form__block">
              <div class="input__group">
                <label for="name-input" class="label">{{ $lang.app.PROJECT_NAME }} <span class="text-red-500">*</span></label>
                <Field name="name" v-slot="{ field, errorMessage }">
                  <input v-bind="field"
                         type="text"
                         id="name-input"
                         v-model="applicationForm.name"
                         class="input"
                         :class="{'input--fail': errorMessage }"
                         :placeholder="$lang.app.ENTER"
                         required>
                  <span v-if="errorMessage" class="input__fail">{{ errorMessage }}</span>
                </Field>
              </div>
              <div class="input__group input__checkbox">
                <input type="checkbox"
                       id="need-additional-expertise-input"
                       class="checkbox"
                       v-model="needAdditionalExpertise"
                       :value="true"/>
                <label for="need-additional-expertise-input" class="label">{{ $lang.app.NEED_TO_PASS_EXTRA_TYPES_OF_EXPERTIZE }}</label>
              </div>
              <div v-if="needAdditionalExpertise" class="input__group">
                <label for="additional-expertise-input" class="label">{{ $lang.app.ADDITIONAL_EXPERTISE }}</label>
                <div class="select__wrapper">
                  <Field name="additionalExpertise" v-slot="{ field, errorMessage }">
                    <Multiselect
                        id="additional-expertise-input"
                        class="select__tags"
                        :attrs="{name: 'additionalExpertise'}"
                        :class="{'select--fail': errorMessage }"
                        v-bind="field"
                        v-model="applicationForm.additionalExpertise"
                        :options="additionalExpertises"
                        mode="tags"
                        :placeholder="$lang.app.SELECT_FROM_OPTIONS"
                        valueProp="id"
                        :searchable="true"
                        :noOptionsText="$lang.app.LIST_EMPTY"
                        :noResultsText="$lang.app.NOT_FOUND"
                        :can-clear="false"
                        required>
                    </Multiselect>
                    <span v-if="errorMessage" class="input__fail">{{ errorMessage }}</span>
                  </Field>
                </div>
              </div>
              <div class="input__group">
                <label for="construction-type-input" class="label">{{ $lang.app.CONSTRUCTION_TYPE }}</label>
                <div class="select__wrapper">
                  <Field name="constructionType" v-slot="{ field, errorMessage }">
                    <Multiselect
                        id="construction-type-input"
                        :attrs="{name: 'constructionType'}"
                        :class="{'select--fail': errorMessage }"
                        v-bind="field"
                        v-model="applicationForm.constructionType"
                        :options="constructionTypes"
                        :placeholder="$lang.app.SELECT_FROM_OPTIONS"
                        valueProp="id"
                        :searchable="true"
                        :noOptionsText="$lang.app.LIST_EMPTY"
                        :noResultsText="$lang.app.NOT_FOUND"
                        :can-clear="false"
                        required>
                    </Multiselect>
                    <span v-if="errorMessage" class="input__fail">{{ errorMessage }}</span>
                  </Field>
                </div>
              </div>
              <div class="input__group input__add__info">
                <p class="label label--custom">{{ $lang.app.OBJECT_POSTAL_ADDRESS }} <span class="text-red-500">*</span></p>
                <template v-if="applicationForm.address">
                  <div class="application__address__value">{{ addressTxt }}</div>
                  <button class="button adding__address button__icon--left button--bordered button--default"
                          @click.prevent="$emitter.emit('open-address-modal')">
                    <svg class="icon" viewBox="0 0 24 24" width="24" height="24">
                      <use xlink:href="@/assets/v2/img/sprite.svg#icon__edit"></use>
                    </svg>
                    <span class="button__text">{{ $lang.app.EDIT_ADDRESS }}</span>
                  </button>
                </template>
                <button v-else type="button" class="button adding__address button__icon--left button--bordered button--default"
                        @click.prevent="$emitter.emit('open-address-modal')">
                  <span class="flex items-center">
                    <svg class="icon" viewBox="0 0 24 24" width="24" height="24">
                      <use xlink:href="@/assets/v2/img/sprite.svg#icon__plus"></use>
                    </svg>
                    <span class="button__text">{{ $lang.app.ADD_ADDRESS }}</span>
                  </span>
                </button>
                <Field class="hidden" name="address" v-model="applicationForm.address"/>
                <ErrorMessage name="address" class="input__fail"/>
                <AddressModal @set-address="setAddress"/>
              </div>
              <div class="input__group">
                <label for="payment-source-input" class="label">{{ $lang.app.EXPERTIZE_PAYMENT_SOURCE }} <span class="text-red-500">*</span></label>
                <div class="select__wrapper">
                  <Field name="paymentSource" v-slot="{ field, errorMessage }">
                    <Multiselect
                        id="payment-source-input"
                        :attrs="{name: 'paymentSource'}"
                        :class="{'select--fail': errorMessage }"
                        v-bind="field"
                        v-model="applicationForm.paymentSource"
                        :options="paymentSourceOptions"
                        :placeholder="$lang.app.SELECT_FROM_OPTIONS"
                        :searchable="true"
                        :noOptionsText="$lang.app.LIST_EMPTY"
                        :noResultsText="$lang.app.NOT_FOUND"
                        :can-clear="false"
                        required>
                    </Multiselect>
                    <span v-if="errorMessage" class="input__fail">{{ errorMessage }}</span>
                  </Field>
                </div>
              </div>
              <div class="input__group input__add__info">
                <label for="ownership-input" class="label">{{ $lang.app.OWNERSHIP_FORM }} <span class="text-red-500">*</span></label>
                <div class="select__wrapper">
                  <Field name="ownership" v-slot="{ field, errorMessage }">
                    <Multiselect
                        id="ownership-input"
                        :class="{'select--fail': errorMessage }"
                        v-bind="field"
                        v-model="applicationForm.ownership"
                        :options="ownershipOptions"
                        :placeholder="$lang.app.SELECT_FROM_OPTIONS"
                        :searchable="true"
                        :noOptionsText="$lang.app.LIST_EMPTY"
                        :noResultsText="$lang.app.NOT_FOUND"
                        :can-clear="false"
                        required>
                    </Multiselect>
                    <span v-if="errorMessage" class="input__fail">{{ errorMessage }}</span>
                  </Field>
                </div>
              </div>
              <div class="add__fields__list">
                <div v-for="(indicator, index) in applicationForm.economicIndicators" :key="index"
                     class="add__field__grid">
                  <div class="add__field__item">
                    <div class="add__field__header">
                      {{ indicator.name }}
                      <button type="button" @click="removeEconomicIndicator(index)">
                        <svg viewBox="0 0 24 24" width="24" height="24">
                          <use xlink:href="@/assets/v2/img/sprite.svg#icon__remove"></use>
                        </svg>
                      </button>
                    </div>
                    <div class="add__field__value__area">
                      {{ indicator.value }} {{ unitOfMeasurements.find(item => item.id === indicator.unitOfMeasurement).unit }}
                    </div>
                    <button type="button" class="add__field__edit" @click.prevent="$emitter.emit('open-indicator-modal', {type: 'EDIT', index, indicator})">
                      <svg viewBox="0 0 24 24" width="24" height="24">
                        <use xlink:href="@/assets/v2/img/sprite.svg#icon__edit"></use>
                      </svg>
                      {{ $lang.app.EDIT }}
                    </button>
                  </div>
                </div>
              </div>
              <div class="input__group">
                <button type="button" class="button button--full button__icon--left button--default button--bordered"
                        @click.prevent="$emitter.emit('open-indicator-modal', {type: 'CREATE'})">
                  <svg class="icon" viewBox="0 0 24 24" width="24" height="24">
                    <use xlink:href="@/assets/v2/img/sprite.svg#icon__plus"></use>
                  </svg>
                  <span class="button__text">{{ $lang.app.MAIN_TECHNO_ECONOMIC_INDICATORS_OF_OBJECT }}</span>
                </button>
              </div>
              <IndicatorModal :unit-of-measurements="unitOfMeasurements"
                              @create-indicator="createIndicator"
                              @edit-indicator="editIndicator"/>
            </div>
          </div>
        </div>
      </div>
    </section>
    <div class="application-create__controls z-10">
      <div class="container-fluid">
        <div class="application-create__controls__wrapper">
          <button type="button" @click="$router.go(-1)" class="button button--cancel">{{ $lang.app.CANCEL }}</button>
          <button type="submit" class="relative button button__icon--right"
                  :disabled="submitting">
            {{ $lang.app.NEXT }}
            <svg class="icon" viewBox="0 0 24 24" width="24" height="24">
              <use xlink:href="@/assets/v2/img/sprite.svg#arrow__right"></use>
            </svg>
            <div v-if="submitting" class="loading-blue text-white absolute z-10 w-full rounded-lg h-full top-0 left-0 flex items-center justify-center">
              <div class="block animate-spin">
                <RefreshIcon class="w-6 h-6 mirror-y"/>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  </Form>
</template>

<script>
import * as yup from "yup";
import {Field, Form, ErrorMessage} from 'vee-validate';
import additionalExpertiseApi from "../../../../api/ref/additionalExpertise";
import constructionTypeApi from "../../../../api/ref/constructionType";
import appointmentApi from "../../../../api/ref/appointment";
import paymentTypeApi from "../../../../api/ref/paymentType";
import applicationApi from "../../../../api/applicant/application";
import {all as ownershipTypesAll} from "../../../../helpers/constants/ownershipTypes";
import AddressModal from "../../../../components/modal/AddressModal.vue";
import IndicatorModal from "../../../../components/applicant/application/modals/IndicatorModal.vue";
import unitOfMeasurementApi from "../../../../api/ref/unitOfMeasurement";

export default {
  name: 'ApplicantApplicationMainCreate',
  components: {
    IndicatorModal,
    Field,
    Form,
    AddressModal,
    ErrorMessage,
  },
  data() {
    return {
      submitting: false,
      additionalExpertises: [],
      constructionTypes: [],
      appointments: [],
      paymentSourceOptions: [],

      needAdditionalExpertise: false,
      addressTxt: '',

      unitOfMeasurements: [],

      applicationForm: {
        name: '',
        additionalExpertise: [],
        constructionType: null,
        isComplete: null,

        address: null,
        paymentSource: null,
        ownership: null,
        economicIndicators: [],
      },

      applicationSchema: yup.object({
        name: yup.string().required(),
        address: yup.mixed().required(),

        paymentSource: yup.mixed().required(),
        ownership: yup.mixed().required(),
        economicIndicators: yup.array().of(yup.object({
          name: yup.string().required(),
          unitOfMeasurement: yup.mixed().required(),
          value: yup.string().required(),
        })),
      }),
    }
  },
  computed: {
    ownershipOptions() {
      return this.ownershipTypesAll().map(option => ({
        label: this.$lang.app[option.labelRef],
        value: option.value
      }))
    }
  },
  created() {
    this.getAdditionalExpertises()
    this.getConstructionTypes()
    this.getAppointments()
    this.getPaymentSourceOptions()
    this.getUnitOfMeasurements();
  },
  watch: {
    needAdditionalExpertise(value) {
      if(!value) {
        this.applicationForm.additionalExpertise = []
      }
    }
  },
  methods: {
    ownershipTypesAll,
    getAdditionalExpertises() {
      additionalExpertiseApi.getAdditionalExpertises()
          .then(response => {
            this.additionalExpertises = response.map(el => {
              return {
                id: el.id,
                label: this.$lang.$translate({ru: el.nameRu, kg: el.nameKg})
              }
            })
          })
          .catch(error => {
            console.log(error);
          })
    },
    getConstructionTypes() {
      constructionTypeApi.getConstructionTypes()
          .then(response => {
            this.constructionTypes = response.map(el => {
              return {
                id: el.id,
                label: this.$lang.$translate({ru: el.nameRu, kg: el.nameKg})
              }
            })
          })
          .catch(error => {
            console.log(error)
          })
    },
    getAppointments() {
      appointmentApi.getAppointments()
          .then(response => {
            this.appointments = response.map(el => {
              return {
                id: el.id,
                label: this.$lang.$translate({ru: el.nameRu, kg: el.nameKg})
              }
            })
          }).catch(error => {
        console.log(error);
      })
    },
    getPaymentSourceOptions() {
      paymentTypeApi.getPaymentTypes()
          .then(response => {
            this.paymentSourceOptions = response.map(el => {
              return {
                value: el.id,
                label: this.$lang.$translate({ru: el.nameRu, kg: el.nameKg})
              }
            })
          }).catch(error => {
        console.log(error);
      })
    },
    getUnitOfMeasurements() {
      unitOfMeasurementApi.getUnitOfMeasurements()
          .then(response => {
            this.unitOfMeasurements = response.map(el => {
              return {id: el.id, label: el.name, unit: el.shortName}
            })
          }).catch(error => {
        console.log(error);
      })
    },
    addEconomicIndicator() {
      this.applicationForm.economicIndicators.push({
        name: '',
        unitOfMeasurement: null,
        value: ''
      });
    },

    removeEconomicIndicator(index) {
      this.applicationForm.economicIndicators.splice(index, 1)
    },

    setAddress(id, addressTxt) {
      this.applicationForm.address = id;
      this.addressTxt = addressTxt;
    },

    createIndicator(payload) {
      this.applicationForm.economicIndicators.push(payload.indicator)
    },

    editIndicator(payload) {
      this.applicationForm.economicIndicators[payload.index] = payload.indicator
    },

    onInvalidSubmit({ errors }) {
      document.querySelector(`[name="${Object.keys(errors)[0]}"]`).focus()
    },

    submit(values, actions) {
      this.submitting = true
      applicationApi.storeApplication(this.applicationForm)
          .then(response => {
            this.$router.push({name: 'ApplicantApplicationApplicantDetail', params: {applicationId: response.data.id}})
            this.$snotify.success(this.$lang.app.SUCCESS_ACTION)
          })
          .catch(error => {
            if (error.response.status === 422) {
              actions.setErrors(error.response.data.errors);
            } else {
              this.$snotify.error(error)
            }
          }).finally(() => {
            this.submitting = false
      })
    }
  }
}
</script>